import { notification } from 'antd';
import dayjs from 'dayjs';

import { DictTypes } from '@/constants';
import type { GetDictContentType } from '@/types/dicts';

import { handleError } from '../handleError';
import { DEPENDED_DICTS_LIST } from './dict';

// Определяем короткосрочные справочники
const shortTermCacheDictTypes = [
  DictTypes.Banks,
  DictTypes.Brands,
  DictTypes.Dealers,
  DictTypes.DealersBrands,
  DictTypes.DealersCities,
  DictTypes.DealersUsedDealers,
  DictTypes.Models,
  DictTypes.Products,
  DictTypes.ProductsTypes,
  DictTypes.SalePoints,
  DictTypes.Statuses
];

// Если главный справочник короткосрочный, делаем все зависимые справочники короткосрочными
DEPENDED_DICTS_LIST.forEach(([mainDictType, dependDictTypes]) => {
  if (shortTermCacheDictTypes.includes(mainDictType)) {
    dependDictTypes.forEach((dependDictType) => {
      if (!shortTermCacheDictTypes.includes(dependDictType)) {
        shortTermCacheDictTypes.push(dependDictType);
      }
    });
  }
});

type ShortTermDictType = (typeof shortTermCacheDictTypes)[number];

// Тип для кэшированных данных
interface CacheType<T = unknown> {
  updateTime: number;
  data: T;
}

// Конфигурация кэша
type CacheDataConfig = {
  shortTerm?: boolean;
};

/**
 * Вычисляет временную метку для следующего обновления кэша
 * @param shortTerm - использовать короткосрочный кэш (1 день) или долгосрочный (до понедельника)
 * @returns временная метка в миллисекундах
 */
const getShiftedDayTimestamp = (shortTerm: boolean): number => {
  const now = dayjs();

  let targetDate: dayjs.Dayjs;

  if (shortTerm) {
    // Добавляем 1 день для короткосрочного кэша
    targetDate = now.add(1, 'day');
  } else {
    // Вычисляем дни до следующего понедельника
    const dayOfWeek = now.day(); // 0 (вс) - 6 (сб)
    const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
    targetDate = now.add(daysUntilMonday, 'day');
  }

  // Устанавливаем 9:00 утра
  return targetDate.hour(9).minute(0).second(0).millisecond(0).valueOf();
};

/**
 * Проверяет, является ли тип справочника короткосрочным
 * @param dictType - тип справочника
 * @returns boolean
 */
const isDictShortTerm = (dictType: DictTypes): dictType is ShortTermDictType =>
  shortTermCacheDictTypes.includes(dictType as ShortTermDictType);

const CACHE_PREFIX = 'cache_';
const createStorageKey = (key: string): string => CACHE_PREFIX + key;

/**
 * Создает объект кэша с данными и временем обновления
 * @param value - данные для кэширования
 * @param shortTerm - тип кэша
 * @returns объект кэша
 */
const createCache = <T>(value: T, shortTerm: boolean): CacheType<T> => ({
  updateTime: getShiftedDayTimestamp(shortTerm),
  data: value
});

/**
 * Проверяет валидность кэшированных данных
 * @param storageValue - строка из хранилища
 * @returns boolean
 */
const isValidCache = (storageValue: string): boolean => {
  try {
    const parsed = JSON.parse(storageValue) as unknown;

    if (!parsed || typeof parsed !== 'object') return false;

    const cache = parsed as Partial<CacheType>;
    return (
      typeof cache.updateTime === 'number' &&
      'data' in cache &&
      new Date().getTime() < cache.updateTime
    );
  } catch {
    return false;
  }
};

/**
 * Сохраняет данные в кэш
 * @param key - ключ кэша
 * @param value - данные для сохранения
 * @param config - конфигурация кэша
 */
export const cacheData = <T>(
  key: string,
  value: T,
  cacheDataConfig?: CacheDataConfig
): void => {
  try {
    const { shortTerm = true } = cacheDataConfig || {};
    const storage = shortTerm ? sessionStorage : localStorage;
    storage.setItem(
      createStorageKey(key),
      JSON.stringify(createCache(value, shortTerm))
    );
  } catch (error) {
    handleError(error, {
      notify: false
    });
  }
};

/**
 * Сохраняет данные справочника в кэш
 * @param dictType - тип справочника
 * @param value - данные справочника
 */
export const cacheDict = <T>(dictType: DictTypes, value: T): void =>
  cacheData(dictType, value, { shortTerm: isDictShortTerm(dictType) });

/**
 * Получает данные из кэша по ключу
 * @param key - ключ кэша
 * @param config - конфигурация кэша
 * @returns данные из кэша или null если кэш невалиден
 */
export const getCachedData = <T>(
  key: string,
  cacheDataConfig?: CacheDataConfig
): T | null => {
  const { shortTerm = true } = cacheDataConfig || {};
  const storage = shortTerm ? sessionStorage : localStorage;

  try {
    const storageValue = storage.getItem(createStorageKey(key));

    if (!storageValue || !isValidCache(storageValue)) {
      storage.removeItem(createStorageKey(key));
      return null;
    }

    const parsed = JSON.parse(storageValue) as CacheType<T>;
    return parsed.data;
  } catch {
    storage.removeItem(createStorageKey(key));
    return null;
  }
};

/**
 * Получает данные справочника из кэша
 * @param dictType - тип справочника
 * @returns данные справочника из кэша или null если кэш невалиден
 */
export const getCachedDict = <T extends DictTypes>(
  dictType: T
): GetDictContentType<T>[] | null =>
  getCachedData<GetDictContentType<T>[]>(dictType, {
    shortTerm: isDictShortTerm(dictType)
  });

const cacheClearedNotificationTrigger = '_isCacheCleared_';

/**
 * Удаляет весь кэш
 */
export const removeCachedData = () => {
  try {
    [sessionStorage, localStorage].forEach((storage) => {
      for (const key in storage) {
        if (key.startsWith(CACHE_PREFIX)) {
          storage.removeItem(key);
        }
      }
    });
  } catch (error) {
    handleError(error, {
      customTitle: 'Произошла ошибка при очистке кэша'
    });
  }
};

/**
 * Проверяет, был ли очищен кэш и показывает уведомление
 */
export const checkCacheClearedAndNotify = () => {
  try {
    if (localStorage.getItem(cacheClearedNotificationTrigger)) {
      localStorage.removeItem(cacheClearedNotificationTrigger);
      notification.success({
        title: 'Кэш успешно очищен'
      });
    }
  } catch (error) {
    handleError(error, {
      notify: false
    });
  }
};
